/**
 * exportRapport.js — Utilitaire d'export pour les rapports Soul Valise
 *
 * Structure attendue pour `rapport` :
 * {
 *   titre:    string,          // Ex: "Chiffre d'affaires"
 *   periode:  { debut, fin },
 *   sections: [
 *     {
 *       titre:    string,      // Ex: "Résumé CA"
 *       colonnes: [{ header: "Libellé", key: "valeur" }, ...],
 *       lignes:   [{ valeur: "10 000 GNF", ... }, ...]
 *     },
 *     ...
 *   ]
 * }
 */

// ── Helpers internes ─────────────────────────────────────────
const nomFichier = (titre, periode, ext) => {
  const d = (periode?.debut || '').replace(/-/g, '');
  const f = (periode?.fin  || '').replace(/-/g, '');
  const slug = titre.toLowerCase().replace(/[^a-z0-9]/g, '_').replace(/_+/g, '_');
  return `soul_valise_${slug}_${d}_${f}.${ext}`;
};

const telecharger = (blob, filename) => {
  const url = URL.createObjectURL(blob);
  const a   = document.createElement('a');
  a.href     = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
};

// ── 1. Export CSV ────────────────────────────────────────────
export function exportCSV(rapport) {
  const lignes = [];

  // En-tête du rapport
  lignes.push(`"${rapport.titre}"`);
  lignes.push(`"Période : ${rapport.periode?.debut || ''} → ${rapport.periode?.fin || ''}"`);
  lignes.push('');

  rapport.sections.forEach(section => {
    // Titre de section
    lignes.push(`"${section.titre}"`);
    // En-têtes colonnes
    lignes.push(section.colonnes.map(c => `"${c.header}"`).join(','));
    // Données
    section.lignes.forEach(row => {
      lignes.push(section.colonnes.map(c => {
        const val = String(row[c.key] ?? '').replace(/"/g, '""');
        return `"${val}"`;
      }).join(','));
    });
    lignes.push('');
  });

  const csv  = '﻿' + lignes.join('\r\n'); // BOM pour Excel français
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  telecharger(blob, nomFichier(rapport.titre, rapport.periode, 'csv'));
}

// ── 2. Export Excel (.xlsx) ──────────────────────────────────
export async function exportExcel(rapport) {
  try {
    const XLSX = await import('xlsx');

    const wb = XLSX.utils.book_new();

    rapport.sections.forEach((section, idx) => {
      // Construire les données de la feuille
      const wsData = [];

      // Ligne titre rapport + période
      wsData.push([rapport.titre, '', `${rapport.periode?.debut || ''} → ${rapport.periode?.fin || ''}`]);
      wsData.push([section.titre]);
      wsData.push([]); // ligne vide

      // En-têtes
      wsData.push(section.colonnes.map(c => c.header));

      // Données
      section.lignes.forEach(row => {
        wsData.push(section.colonnes.map(c => row[c.key] ?? ''));
      });

      const ws = XLSX.utils.aoa_to_sheet(wsData);

      // Largeur auto des colonnes
      const colWidths = section.colonnes.map(c => ({
        wch: Math.max(c.header.length, 18)
      }));
      ws['!cols'] = colWidths;

      // Nom de l'onglet (max 31 chars)
      const sheetName = section.titre.slice(0, 31);
      XLSX.utils.book_append_sheet(wb, ws, sheetName || `Feuille ${idx + 1}`);
    });

    const buf  = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
    const blob = new Blob([buf], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
    telecharger(blob, nomFichier(rapport.titre, rapport.periode, 'xlsx'));
  } catch (err) {
    console.error('Erreur export Excel:', err);
    throw new Error('Impossible de générer le fichier Excel');
  }
}

// ── 3. Export PDF ────────────────────────────────────────────
export async function exportPDF(rapport) {
  try {
    const { default: jsPDF }    = await import('jspdf');
    const { default: autoTable } = await import('jspdf-autotable');

    const doc = new jsPDF({ orientation: 'landscape', unit: 'mm', format: 'a4' });
    const pageW = doc.internal.pageSize.getWidth();

    // Palette couleurs
    const INDIGO = [99, 102, 241];
    const GRAY   = [107, 114, 128];
    const WHITE  = [255, 255, 255];
    const LIGHT  = [248, 250, 252];

    let cursorY = 15;

    rapport.sections.forEach((section, idx) => {
      if (idx > 0) {
        doc.addPage();
        cursorY = 15;
      }

      // ── En-tête de page ──────────────────────────────
      doc.setFillColor(...INDIGO);
      doc.rect(0, 0, pageW, 22, 'F');

      doc.setTextColor(...WHITE);
      doc.setFontSize(16);
      doc.setFont('helvetica', 'bold');
      doc.text((nomEntreprise || 'Mon entreprise'), 14, 10);

      doc.setFontSize(11);
      doc.setFont('helvetica', 'normal');
      doc.text(rapport.titre, 14, 17);

      doc.setFontSize(9);
      const periode = `Période : ${rapport.periode?.debut || ''} → ${rapport.periode?.fin || ''}`;
      doc.text(periode, pageW - 14, 17, { align: 'right' });

      cursorY = 30;

      // ── Titre de section ─────────────────────────────
      doc.setTextColor(...INDIGO);
      doc.setFontSize(12);
      doc.setFont('helvetica', 'bold');
      doc.text(section.titre, 14, cursorY);
      cursorY += 6;

      // ── Tableau ──────────────────────────────────────
      const head   = [section.colonnes.map(c => c.header)];
      const body   = section.lignes.map(row =>
        section.colonnes.map(c => String(row[c.key] ?? ''))
      );

      autoTable(doc, {
        startY:          cursorY,
        head,
        body,
        theme:           'grid',
        headStyles: {
          fillColor:     INDIGO,
          textColor:     WHITE,
          fontStyle:     'bold',
          fontSize:      9,
          halign:        'center',
        },
        bodyStyles: {
          fontSize:      8.5,
          textColor:     [30, 30, 30],
          cellPadding:   2.5,
        },
        alternateRowStyles: { fillColor: LIGHT },
        columnStyles:    { 0: { fontStyle: 'bold' } },
        margin:          { left: 14, right: 14 },
        didDrawPage: (data) => {
          // Pied de page
          const pageCount = doc.internal.getNumberOfPages();
          doc.setFontSize(8);
          doc.setTextColor(...GRAY);
          doc.setFont('helvetica', 'normal');
          doc.text(
            `${nomEntreprise || 'Entreprise'} — ${rapport.titre} — Page ${data.pageNumber} / ${pageCount}`,
            pageW / 2, doc.internal.pageSize.getHeight() - 8,
            { align: 'center' }
          );
          const now = new Date().toLocaleDateString('fr-FR', { day:'2-digit', month:'2-digit', year:'numeric', hour:'2-digit', minute:'2-digit' });
          doc.text(`Généré le ${now}`, pageW - 14, doc.internal.pageSize.getHeight() - 8, { align: 'right' });
        },
      });

      cursorY = doc.lastAutoTable.finalY + 10;
    });

    doc.save(nomFichier(rapport.titre, rapport.periode, 'pdf'));
  } catch (err) {
    console.error('Erreur export PDF:', err);
    throw new Error('Impossible de générer le fichier PDF');
  }
}

// ── 4. Export Word (.doc via HTML) ────────────────────────────
export function exportWord(rapport) {
  const htmlSections = rapport.sections.map(section => {
    const thead = `<tr>${section.colonnes.map(c => `<th>${c.header}</th>`).join('')}</tr>`;
    const tbody = section.lignes.map(row =>
      `<tr>${section.colonnes.map(c => `<td>${row[c.key] ?? ''}</td>`).join('')}</tr>`
    ).join('');

    return `
      <h2>${section.titre}</h2>
      <table border="1" cellspacing="0" cellpadding="5" style="border-collapse:collapse;width:100%;font-size:11pt;">
        <thead style="background-color:#6366f1;color:white;">${thead}</thead>
        <tbody>${tbody}</tbody>
      </table>
      <br/>
    `;
  }).join('');

  const html = `
    <html xmlns:o="urn:schemas-microsoft-com:office:office"
          xmlns:w="urn:schemas-microsoft-com:office:word"
          xmlns="http://www.w3.org/TR/REC-html40">
    <head>
      <meta charset="UTF-8"/>
      <meta name=ProgId content=Word.Document/>
      <meta name=Generator content="Microsoft Word 15"/>
      <title>${rapport.titre}</title>
      <style>
        body { font-family: Arial, sans-serif; font-size: 11pt; margin: 2cm; }
        h1   { color: #6366f1; font-size: 18pt; margin-bottom: 4pt; }
        h2   { color: #4f46e5; font-size: 13pt; margin-top: 20pt; margin-bottom: 6pt; }
        p.periode { color: #6b7280; font-size: 10pt; margin-bottom: 16pt; }
        table { border-collapse: collapse; width: 100%; margin-bottom: 16pt; }
        th    { background-color: #6366f1; color: white; padding: 6pt 8pt; font-size: 10pt; text-align: left; }
        td    { padding: 5pt 8pt; font-size: 10pt; border: 1pt solid #d1d5db; }
        tr:nth-child(even) td { background-color: #f8fafc; }
        .footer { color: #9ca3af; font-size: 9pt; margin-top: 24pt; text-align: center; }
      </style>
    </head>
    <body>
      <h1>${rapport.titre}</h1>
      <p class="periode">Période : ${rapport.periode?.debut || ''} → ${rapport.periode?.fin || ''}</p>
      <p class="periode">Généré le : ${new Date().toLocaleDateString('fr-FR', { weekday:'long', year:'numeric', month:'long', day:'numeric' })}</p>
      ${htmlSections}
      <p class="footer">${nomEntreprise || 'Entreprise'} — Rapport généré automatiquement</p>
    </body>
    </html>
  `;

  const blob = new Blob([html], { type: 'application/msword;charset=utf-8' });
  telecharger(blob, nomFichier(rapport.titre, rapport.periode, 'doc'));
}

// ── 5. Fonction centrale d'export ───────────────────────────
export async function exporter(format, rapport, onError) {
  try {
    switch (format) {
      case 'csv':   exportCSV(rapport);          break;
      case 'xlsx':  await exportExcel(rapport);  break;
      case 'pdf':   await exportPDF(rapport);    break;
      case 'word':  exportWord(rapport);         break;
      default: throw new Error(`Format inconnu : ${format}`);
    }
  } catch (err) {
    console.error(err);
    if (onError) onError(err.message || 'Erreur lors de l\'export');
  }
}
